import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { Z_INDEX } from '../../styles/zIndex';
import styles from './HoverDropdown.module.css';

interface HoverDropdownProps {
  /** トリガーボタンの内容 */
  trigger: ReactNode;
  /** ドロップダウンメニューの内容 */
  children: ReactNode;
  /** ドロップダウンの位置（デフォルト: 'top'） */
  position?: 'top' | 'bottom';
  /** ドロップダウンの水平方向の配置（デフォルト: 'left'） */
  align?: 'left' | 'right';
  /** ドロップダウンの最小幅（デフォルト: '200px'） */
  minWidth?: string;
  /** ドロップダウンとトリガー間の距離（デフォルト: '8px'） */
  gap?: string;
}

/**
 * ホバーで開くドロップダウンメニューコンポーネント（PC専用）
 *
 * 使用例:
 * ```tsx
 * <HoverDropdown
 *   trigger={<button>メニューを開く</button>}
 *   position="top"
 * >
 *   <button onClick={() => console.log('clicked')}>オプション1</button>
 *   <button onClick={() => console.log('clicked')}>オプション2</button>
 * </HoverDropdown>
 * ```
 */
export function HoverDropdown({
  trigger,
  children,
  position = 'top',
  align = 'left',
  minWidth = '200px',
  gap = '8px',
}: HoverDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // マウスがトリガーまたはドロップダウンに入ったら開く
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOpen(true);
  };

  // マウスが離れたら少し遅延して閉じる（ドロップダウンに移動する時間を確保）
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 100);
  };

  // 外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', zIndex: isOpen ? Z_INDEX.popover : 'auto' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {trigger}
      {isOpen && (
        <div
          className="modal-surface"
          style={{
            position: 'absolute',
            [position === 'top' ? 'bottom' : 'top']: `calc(100% + ${gap})`,
            ...(align === 'right' ? { right: 0 } : { left: 0 }),
            minWidth,
            zIndex: Z_INDEX.popover,
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(false);
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

interface HoverDropdownItemProps {
  /** クリック時の処理 */
  onClick: () => void;
  /** ボタンの内容 */
  children: ReactNode;
  /** アイコン（オプション） */
  icon?: ReactNode;
  /** 無効化（オプション） */
  disabled?: boolean;
}

/**
 * HoverDropdownで使用するメニュー項目コンポーネント
 */
export function HoverDropdownItem({ onClick, children, icon, disabled }: HoverDropdownItemProps) {
  return (
    <button
      type="button"
      className={styles.dropdownItem}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
    >
      {icon && <span>{icon}</span>}
      {children}
    </button>
  );
}

/**
 * HoverDropdownで使用する区切り線コンポーネント
 */
export function HoverDropdownDivider() {
  return <div className={styles.divider} />;
}

interface ClickDropdownProps {
  /** トリガーボタンの内容 */
  trigger: ReactNode;
  /** ドロップダウンメニューの内容 */
  children: ReactNode;
  /** ドロップダウンの位置（デフォルト: 'top'） */
  position?: 'top' | 'bottom';
  /** ドロップダウンの水平方向の配置（デフォルト: 'left'） */
  align?: 'left' | 'right';
  /** ドロップダウンの最小幅（デフォルト: '200px'） */
  minWidth?: string;
  /** ドロップダウンとトリガー間の距離（デフォルト: '8px'） */
  gap?: string;
  /** メニューを document.body にポータル描画する（祖先の overflow / contain /
   *  stacking context に閉じ込められるのを避けたいときに使う）。 */
  usePortal?: boolean;
}

/**
 * クリック/タップで開くドロップダウンメニューコンポーネント（スマホ用）
 */
export function ClickDropdown({
  trigger,
  children,
  position = 'top',
  align = 'left',
  minWidth = '200px',
  gap = '8px',
  usePortal = false,
}: ClickDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [resolvedPosition, setResolvedPosition] = useState(position);
  const [resolvedAlign, setResolvedAlign] = useState(align);
  // ポータル描画時のトリガー位置（fixed 配置の基準）。
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);

  // クリック/タップでトグル
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen((prev) => {
      const next = !prev;
      if (next && usePortal && containerRef.current) {
        setTriggerRect(containerRef.current.getBoundingClientRect());
      }
      return next;
    });
  };

  // 開いた直後にドロップダウンが画面内に収まるか判定し、収まらなければ反転
  useEffect(() => {
    if (!isOpen) {
      setResolvedPosition(position);
      setResolvedAlign(align);
      return;
    }
    const dropdown = dropdownRef.current;
    if (!dropdown) return;
    const rect = dropdown.getBoundingClientRect();
    // 垂直方向
    if (position === 'top' && rect.top < 0) {
      setResolvedPosition('bottom');
    } else if (position === 'bottom' && rect.bottom > window.innerHeight) {
      setResolvedPosition('top');
    }
    // 水平方向
    if (rect.left < 0) {
      setResolvedAlign('left');
    } else if (rect.right > window.innerWidth) {
      setResolvedAlign('right');
    }
  }, [isOpen, position, align]);

  // 外側タップで閉じる
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    // 次のイベントループで登録（同一タップでの誤閉じを防ぐ）
    const timerId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  // 見た目(背景/枠線/角丸/影)は共通クラス .modal-surface に集約。ここはレイアウトのみ。
  const menuVisualStyle = {
    minWidth,
    zIndex: Z_INDEX.popover,
  } as const;

  const menuContent = (
    <div
      ref={dropdownRef}
      className="modal-surface"
      style={
        usePortal
          ? {
              position: 'fixed',
              ...(triggerRect
                ? resolvedPosition === 'top'
                  ? { bottom: window.innerHeight - triggerRect.top + (parseFloat(gap) || 0) }
                  : { top: triggerRect.bottom + (parseFloat(gap) || 0) }
                : {}),
              ...(triggerRect
                ? resolvedAlign === 'right'
                  ? { right: window.innerWidth - triggerRect.right }
                  : { left: triggerRect.left }
                : {}),
              ...menuVisualStyle,
            }
          : {
              position: 'absolute',
              [resolvedPosition === 'top' ? 'bottom' : 'top']: `calc(100% + ${gap})`,
              ...(resolvedAlign === 'right' ? { right: 0 } : { left: 0 }),
              ...menuVisualStyle,
            }
      }
      onClick={(e) => {
        e.stopPropagation();
        setIsOpen(false);
      }}
    >
      {children}
    </div>
  );

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', zIndex: isOpen && !usePortal ? Z_INDEX.popover : 'auto' }}
      onClick={handleClick}
    >
      {trigger}
      {isOpen && (usePortal ? createPortal(menuContent, document.body) : menuContent)}
    </div>
  );
}
